import { Box, Typography } from '@oresundsbron/bridge-ui';
import { pipe } from 'fp-ts/lib/function';
import {
  chain,
  fromNullable,
  map,
  fromPredicate,
  alt,
  getOrElse,
} from 'fp-ts/lib/Option';
import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { usePrivateITicketData } from './stores/usePrivateITicketData';

const Internal: FC = () => {
  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });

  return (
    <Box className="col-span-full md:col-span-6">
      <Typography as="h2" intent="title" gutter>
        Error
      </Typography>
      <Typography>{t('errors.generic')}</Typography>
    </Box>
  );
};

const NotFound: FC = () => {
  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });

  return (
    <Box className="col-span-full md:col-span-6">
      <Typography as="h2" intent="title" gutter>
        {t('refund.notFound.title')}
      </Typography>
      <Typography>{t('refund.notFound.description')}</Typography>
    </Box>
  );
};

const RefundFailed: FC = () => {
  const { t } = useTranslation(['onlineTicket', 'common'], {
    nsMode: 'fallback',
  });

  return (
    <Box className="col-span-full md:col-span-6">
      <Typography as="h2" intent="title" gutter>
        {t('refund.error.title')}
      </Typography>
      <Typography>{t('refund.error.description')}</Typography>
    </Box>
  );
};

const renderNotFound = (err: string) =>
  pipe(
    err,
    fromPredicate((x) => x === 'REFUND_NOT_FOUND'),
    map(() => <NotFound />)
  );

const renderRefundFailed = (err: string) =>
  pipe(
    err,
    fromPredicate((x) => x === 'REFUND_FAILED'),
    map(() => <RefundFailed />)
  );

const renderError = (err: string) =>
  pipe(
    err,
    renderNotFound,
    alt(() => pipe(err, renderRefundFailed))
  );

export const Error: FC = () => {
  const { error } = usePrivateITicketData();

  const content = useMemo(
    () =>
      pipe(
        error,
        fromNullable,
        map((err) => err.error),
        chain(fromNullable),
        chain(renderError),
        getOrElse(() => <Internal />)
      ),
    [error]
  );

  return content;
};
